/*
 * Live case-colour configurator.
 *
 * Recolours the case in the browser instead of pre-rendering a PSD per
 * colourway. Per pixel:
 *
 *     out = ( colour*(1-a) + artwork*a ) * mul + add
 *
 * `a` and `artwork` are sampled from the flat artwork through a coordinate
 * map, so the print warps around the case exactly as the mockup does. Ported
 * from the reference implementation in
 * "E:\Ski phone\6 - Preview assets\case-configurator.html" -- the maths there
 * is proven, do not re-derive it.
 *
 * Everything is document-level delegation because the theme replaces both
 * [data-product-media-container] and every [data-product-block] on a variant
 * change; bound listeners and painted canvases do not survive that, so the
 * chosen colour lives in module state and is re-applied on remount.
 */
(function () {
  'use strict';

  // Must stay this exact English string in every locale -- the order pipeline
  // matches on it, the same way the case-type picker does.
  var PROP = 'Case Colour';

  // Below this brightness the artwork with white text is used, above it black.
  // Same constant as the Photoshop side, so preview and print agree.
  var LUM_THRESHOLD = 0.35;

  /*
   * THE COORDINATE MAP IS NOT AN IMAGE. DO NOT MAKE IT ONE AGAIN.
   *
   * It used to ship as mk_uv + mk_uvlo, one 16-bit number per pixel split
   * across two bytes. That cannot survive Shopify's CDN: the browser sends
   * `Accept: image/webp`, the CDN transcodes, and the transcode is LOSSY.
   * Measured on this shop, a 4.9 MB PNG comes back as a 410 KB WebP. For a
   * photo that is invisible; for the map it is fatal, because every unit the
   * low byte is perturbed by is a real displacement in the artwork. The print
   * came back as illegible mush.
   *
   * So the warp now ships as polynomial coefficients (mockup.uv_poly, ~900
   * bytes of JSON) and is evaluated here, once, into two Float32Arrays.
   * Verified against the lossless local PNGs at a median residual of 0.4px of
   * artwork -- the coefficients ARE the map, not an approximation of it.
   *
   * Resampling is the same trap in a different coat:
   *  - the map must never be resized (there is no correct resize for a warp
   *    field sampled per output pixel),
   *  - the artwork is sampled rather than drawn, so shrinking it blurs the
   *    small spec text before it is ever placed.
   *
   * Three 1300x1300 plates plus the artwork is roughly 40 MB of ImageData. If
   * a slow device cannot afford that, the lever is compositing LESS OFTEN --
   * debounce the picker, skip frames while dragging -- never resampling
   * anything. Do not reintroduce a PLATE_MAX.
   */

  var state = { hex: null, name: null };
  var plates = null;              // {add, mul, u, v, mask, w, h}
  var art = {};                   // {light: <decoded>, dark: <decoded>}
  var platesPromise = null;
  var artPromise = {};
  // Make Your Own Case: the light/dark plates arrive from the preview API at
  // runtime (window.ccSetArt), not from the metafield. Same two images a
  // premade product carries; the maths below does not know the difference.
  var runtimeArt = null;          // {light, dark} urls, or null
  // MYOC mode (config.myoc): the chosen colour goes into the customer's own
  // "Custom Color" text field -- the property logic.py reads for MYOC -- and
  // NOT into a Case Colour property.
  //
  // `silent` describes ONE CALL, never the state. The preview re-applies the
  // current colour silently every time it finishes (myoc-preview.js done()),
  // and a sticky flag there meant the field stopped being maintained: the
  // next Case Type change rebuilt it empty and nothing wrote it back, so the
  // colour vanished from the cart. What matters is whether the CUSTOMER ever
  // picked one -- that is what owns the property.
  var userPicked = false;
  var fieldWritten = null;        // what we last wrote into the MYOC field
  var off = null, offCtx = null, buffer = null;
  var frame = 0;
  var failed = false;
  var exported = null;            // cached toDataURL of the current render

  function config() {
    var el = document.querySelector('[data-cc-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function strings() {
    var c = config();
    return (c && c.strings) || {};
  }

  function normalize(value) {
    if (!value) return null;
    var v = String(value).trim();
    if (v.charAt(0) !== '#') v = '#' + v;
    return /^#[0-9a-f]{6}$/i.test(v) ? v.toUpperCase() : null;
  }

  /* ------------------------------------------------------------- loading -- */

  function decode(url, opts) {
    opts = opts || {};
    return new Promise(function (resolve, reject) {
      var im = new Image();
      // Shopify's CDN sends Access-Control-Allow-Origin, and without this the
      // canvas is tainted and getImageData throws.
      im.crossOrigin = 'anonymous';
      im.onload = function () {
        // Native size unless a caller forces a match. There is deliberately no
        // "shrink to fit" option -- see the note at the top of this file.
        var w = opts.forceW || im.naturalWidth;
        var h = opts.forceH || im.naturalHeight;
        var c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        var g = c.getContext('2d', { willReadFrequently: true });
        g.drawImage(im, 0, 0, w, h);
        resolve({ data: g.getImageData(0, 0, w, h).data, w: w, h: h });
      };
      im.onerror = function () { reject(new Error('could not load ' + url)); };
      im.src = url;
    });
  }

  function loadPlates() {
    if (platesPromise) return platesPromise;
    var cfg = config();
    if (!cfg || !cfg.assets || !cfg.assets.mockup) {
      return Promise.reject(new Error('no preview assets'));
    }
    var mk = cfg.assets.mockup;
    if (!mk.uv_poly) return Promise.reject(new Error('no uv_poly on this product'));

    // mk_add sets the working resolution; mk_mul is forced to match it. Both
    // are ordinary images -- smooth shading and reflections -- so the CDN's
    // WebP transcode is a quality question here, not a correctness one.
    platesPromise = decode(mk.add).then(function (add) {
      return decode(mk.mul, { forceW: add.w, forceH: add.h }).then(function (mul) {
        var warp = evaluatePoly(mk.uv_poly, add.w, add.h);
        plates = {
          add: add, mul: mul, u: warp.u, v: warp.v,
          mask: printMask(add, mul), w: add.w, h: add.h
        };
        off = document.createElement('canvas');
        off.width = plates.w;
        off.height = plates.h;
        offCtx = off.getContext('2d');
        buffer = offCtx.createImageData(plates.w, plates.h);
        return plates;
      });
    });
    return platesPromise;
  }

  // The warp, evaluated once into two planes the paint loop can just index.
  // Done separably: collapse the y half of the polynomial per row (7 values),
  // then one pass along x. That is 7 multiply-adds per pixel per axis instead
  // of 28, which is the difference between a noticeable pause and not.
  function evaluatePoly(poly, W, H) {
    var n = poly.deg + 1;
    var cu = new Float64Array(n * n), cv = new Float64Array(n * n);
    for (var t = 0; t < poly.terms.length; t++) {
      var i = poly.terms[t][0], j = poly.terms[t][1];
      cu[i * n + j] = poly.u[t];
      cv[i * n + j] = poly.v[t];
    }

    // fx^i for every column, computed once
    var px = new Float64Array(W * n);
    for (var x = 0; x < W; x++) {
      var fx = (x / W) - 0.5, p = 1;
      for (var a = 0; a < n; a++) { px[x * n + a] = p; p *= fx; }
    }

    var U = new Float32Array(W * H), V = new Float32Array(W * H);
    var gu = new Float64Array(n), gv = new Float64Array(n), py = new Float64Array(n);
    for (var y = 0; y < H; y++) {
      var fy = (y / H) - 0.5, q = 1;
      for (var b = 0; b < n; b++) { py[b] = q; q *= fy; }
      for (var c = 0; c < n; c++) {
        var su = 0, sv = 0;
        for (var d = 0; d < n; d++) {
          su += cu[c * n + d] * py[d];
          sv += cv[c * n + d] * py[d];
        }
        gu[c] = su; gv[c] = sv;
      }
      var row = y * W;
      for (var x2 = 0; x2 < W; x2++) {
        var base = x2 * n, u = 0, v = 0;
        for (var e = 0; e < n; e++) {
          var bx = px[base + e];
          u += gu[e] * bx;
          v += gv[e] * bx;
        }
        U[row + x2] = u; V[row + x2] = v;
      }
    }
    return { u: U, v: V };
  }

  // The printable area used to be mk_uv's alpha channel. Same test the
  // coefficients were fitted with; agrees with the old alpha on 99.8% of
  // pixels, measured against the local plates.
  function printMask(add, mul) {
    var ad = add.data, mu = mul.data;
    var n = add.w * add.h;
    var mask = new Uint8Array(n);
    for (var p = 0, i = 0; p < n; p++, i += 4) {
      if (ad[i + 3] > 200 && (mu[i] + mu[i + 1] + mu[i + 2]) > 114.75) mask[p] = 1;
    }
    return mask;
  }

  function isMyoc() {
    var c = config();
    return !!(c && c.myoc);
  }

  /*
   * MAKE YOUR OWN CASE ALWAYS HAS A COLOUR.
   *
   * There is no "original design" to fall back on here: every custom case is
   * printed on some background, and an order that carries no colour tells the
   * designer nothing -- it happened on #2877 (6 Sept 2026) and the colour had
   * to be guessed. Production already treats "no colour" as white, so the
   * default is stated rather than implied: the picker starts on the palette's
   * white, the cart line shows it, and the case is printed in it.
   *
   * Taken from the palette in the config, so this cannot drift from the
   * swatch the customer sees selected.
   */
  var MYOC_DEFAULT_HEX = '#F2F2F2';

  function myocDefault() {
    var c = config();
    var pal = (c && c.palette) || [];
    for (var i = 0; i < pal.length; i++) {
      if (normalize(pal[i].hex) === MYOC_DEFAULT_HEX) return pal[i];
    }
    return pal.length ? pal[0] : null;
  }

  // Sets the colour WITHOUT starting a paint: plates are several megabytes
  // and are still only fetched on the first real interaction.
  function applyMyocDefault() {
    var d = myocDefault();
    if (!d) return false;
    var hex = normalize(d.hex);
    if (!hex) return false;
    state.hex = hex;
    state.name = d.name || null;
    state.silent = false;
    userPicked = true;
    return true;
  }

  function loadArt(kind) {
    if (art[kind]) return Promise.resolve(art[kind]);
    if (artPromise[kind]) return artPromise[kind];
    var cfg = config();
    var url = (runtimeArt && runtimeArt[kind]) || (cfg && cfg.assets && cfg.assets[kind]);
    if (!url) return Promise.resolve(null);
    artPromise[kind] = decode(url, {}).then(function (img) {
      art[kind] = img;
      return img;
    });
    return artPromise[kind];
  }

  function otherKind(kind) { return kind === 'dark' ? 'light' : 'dark'; }

  /* -------------------------------------------------------------- render -- */

  function artFor(hex) {
    var h = hex.slice(1);
    var r = parseInt(h.slice(0, 2), 16),
        g = parseInt(h.slice(2, 4), 16),
        b = parseInt(h.slice(4, 6), 16);
    // Black or white text, whichever has the higher WCAG contrast with the case
    // colour (4 Oct 2026). The old 0.35 brightness cut gave a #2E7031 green
    // black text at 3.5:1 when white gave 6.0:1. MATCHED with CaseColour.jsx
    // (ccWantsDarkText) and preview_composer.py (wants_dark_text) -- change all
    // three together or the print stops matching this preview.
    function ch(v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }
    var L = 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
    var dark = (L + 0.05) / 0.05 > 1.05 / (L + 0.05);
    return { r: r, g: g, b: b, kind: dark ? 'dark' : 'light' };
  }

  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(function () {
      frame = 0;
      paint();
    });
  }

  function paint() {
    if (!state.hex || !plates) return;
    var c = artFor(state.hex);
    var source = art[c.kind] || art[otherKind(c.kind)];
    if (!source) {
      loadArt(c.kind).then(function (loaded) { if (loaded) schedule(); });
      return;
    }
    // Wanted white text but only black is decoded (or the reverse) -- show what
    // we have now and repaint the moment the right plate arrives.
    if (!art[c.kind]) loadArt(c.kind).then(function (l) { if (l) schedule(); });

    var ac = plates.add.data, mu = plates.mul.data,
        U = plates.u, V = plates.v, mask = plates.mask;
    var AW = source.w, AH = source.h, ad = source.data;
    var o = buffer.data;
    var CR = c.r, CG = c.g, CB = c.b;

    for (var p = 0, i = 0; i < o.length; p++, i += 4) {
      var caseA = ac[i + 3];
      if (caseA === 0) { o[i + 3] = 0; continue; }

      var mr = mu[i] / 255, mg = mu[i + 1] / 255, mb = mu[i + 2] / 255;

      if (!mask[p]) {
        // Outside the printable area -- rebuild the plain white-texture render.
        o[i] = ac[i] + 255 * mr;
        o[i + 1] = ac[i + 1] + 255 * mg;
        o[i + 2] = ac[i + 2] + 255 * mb;
        o[i + 3] = caseA;
        continue;
      }

      // Where this pixel came from on the flat artwork. The warp planes carry
      // the same 0-255 units the old 16-bit PNG pair did; the polynomial is
      // continuous, so there is no quantisation grid to snap to any more.
      var fu = U[p] * (1 / 255), fv = V[p] * (1 / 255);
      if (fu < 0) fu = 0; else if (fu > 1) fu = 1;
      if (fv < 0) fv = 0; else if (fv > 1) fv = 1;
      var fx = fu * (AW - 1), fy = fv * (AH - 1);
      var x0 = fx | 0, y0 = fy | 0;
      if (x0 > AW - 2) x0 = AW - 2;
      if (y0 > AH - 2) y0 = AH - 2;
      var tx = fx - x0, ty = fy - y0,
          p00 = (y0 * AW + x0) << 2, p10 = p00 + 4,
          p01 = p00 + (AW << 2), p11 = p01 + 4;

      // Bilinear, so edges stay smooth instead of stair-stepping.
      var w00 = (1 - tx) * (1 - ty), w10 = tx * (1 - ty),
          w01 = (1 - tx) * ty, w11 = tx * ty;
      var ar = ad[p00] * w00 + ad[p10] * w10 + ad[p01] * w01 + ad[p11] * w11,
          ag = ad[p00 + 1] * w00 + ad[p10 + 1] * w10 + ad[p01 + 1] * w01 + ad[p11 + 1] * w11,
          ab = ad[p00 + 2] * w00 + ad[p10 + 2] * w10 + ad[p01 + 2] * w01 + ad[p11 + 2] * w11,
          aa = ad[p00 + 3] * w00 + ad[p10 + 3] * w10 + ad[p01 + 3] * w01 + ad[p11 + 3] * w11;

      var a = aa / 255;
      o[i] = (CR * (1 - a) + ar * a) * mr + ac[i];
      o[i + 1] = (CG * (1 - a) + ag * a) * mg + ac[i + 1];
      o[i + 2] = (CB * (1 - a) + ab * a) * mb + ac[i + 2];
      o[i + 3] = caseA;
    }

    offCtx.putImageData(buffer, 0, 0);
    blit();
  }

  // Desktop and mobile galleries are both in the DOM at all times, toggled by
  // CSS, so there is more than one overlay to fill.
  function blit() {
    var list = document.querySelectorAll('[data-cc-overlay]');
    for (var i = 0; i < list.length; i++) {
      var ov = list[i];
      var cv = ov.querySelector('canvas');
      if (!cv) {
        cv = document.createElement('canvas');
        cv.className = 'cc-overlay__canvas';
        ov.appendChild(cv);
      }
      if (cv.width !== off.width || cv.height !== off.height) {
        cv.width = off.width;
        cv.height = off.height;
      }
      cv.getContext('2d').drawImage(off, 0, 0);

      // "Your colour - Alpine blue". Without it the recoloured slide reads as
      // an inconsistency next to the stock photos; with it, it reads as the
      // customer's own case. Hidden on thumbnails by CSS -- too small to help.
      var tag = ov.querySelector('[data-cc-tag]');
      if (tag) {
        tag.textContent = (strings().your_colour || 'Your colour') + ' · ' +
          (state.name || state.hex);
        tag.hidden = false;
      }
      ov.hidden = false;
    }
    exported = null;   // the canvas changed, so any cached export is stale
    // myoc-preview.js mirrors the gallery canvas into a thumbnail
    try { document.dispatchEvent(new CustomEvent('cc:painted')); } catch (e) {}
  }

  function hideOverlays() {
    var list = document.querySelectorAll('[data-cc-overlay]');
    for (var i = 0; i < list.length; i++) {
      list[i].hidden = true;
      var tag = list[i].querySelector('[data-cc-tag]');
      if (tag) tag.hidden = true;
    }
    exported = null;
  }

  /* ------------------------------------------------------------------ ui -- */

  // Name first so the cart, checkout and confirmation email read naturally;
  // hex in brackets so the order pipeline can recover the exact colour.
  function propertyValue() {
    if (!state.hex) return '';
    return state.name ? state.name + ' (' + state.hex + ')' : state.hex;
  }

  /*
   * What the Case Colour property carries on a premade case:
   *
   *     Alpine blue (#1B4F9C) – printed in this colour, not as pictured
   *
   * Checkout cannot show a coloured picture (only Plus stores can change it),
   * so the cart keeps the product photo too and this sentence is what the
   * customer reads, word for word, in cart, checkout, the order page and the
   * emails. A customer once picked a colour, saw the white photo and assumed
   * white; this is what he should have read.
   *
   * The property NAME is untouched and "(#RRGGBB)" is kept exactly: the order
   * pipeline reads the first #hex in the value and nothing after it.
   * Make Your Own Case does not use this -- its preview IS the picture.
   */
  function casePropertyValue() {
    var v = propertyValue();
    var suffix = strings().suffix;
    return v && suffix ? v + ' \u2013 ' + suffix : v;
  }

  // "Add to cart – Alpine blue case". Only ever over the theme's plain
  // add-to-cart wording (never over Sold out / Pre-order), and put back the
  // moment the colour is reset or the button stops being buyable.
  function syncAddButton(chosen) {
    var s = strings();
    var inp = document.querySelector('[data-cc-input]');
    var form = inp && inp.closest ? inp.closest('form') : null;
    if (!form) return;
    var plain = String(s.atc_default || '').trim().toLowerCase();
    var btns = form.querySelectorAll('button[name="add"]');
    for (var i = 0; i < btns.length; i++) {
      var label = btns[i].querySelector('[data-button-text]');
      if (!label) continue;
      var cur = label.textContent.trim();
      var shown = label.getAttribute('data-cc-atc-shown');
      var original = shown !== null && cur === shown ? label.getAttribute('data-cc-atc-original') : cur;
      var want = null;
      if (chosen && !isMyoc() && s.atc && !btns[i].disabled && plain && String(original).trim().toLowerCase() === plain) {
        want = s.atc.replace('%COLOUR%', state.name || state.hex);
      }
      if (want) {
        if (cur !== want) {
          label.setAttribute('data-cc-atc-original', original);
          label.setAttribute('data-cc-atc-shown', want);
          label.textContent = want;
        }
      } else if (shown !== null) {
        if (cur === shown) label.textContent = label.getAttribute('data-cc-atc-original') || cur;
        label.removeAttribute('data-cc-atc-original');
        label.removeAttribute('data-cc-atc-shown');
      }
    }
  }

  function status(text) {
    var list = document.querySelectorAll('[data-cc-status]');
    for (var i = 0; i < list.length; i++) {
      list[i].textContent = text || '';
      list[i].hidden = !text;
    }
  }

  /*
   * The picker sits inside <variant-picker>, which does
   *
   *     handleVariantChange = e => {
   *       if (e.target.name.includes("properties")) return;
   *       this.swapProduct(this.getFetchUrl(e.target));
   *     }
   *
   * on every `change` beneath it. That guard only saves inputs with a name --
   * the colour input has none, so dismissing the OS colour sheet would fetch
   * and rebuild the whole product section, throwing the canvas away each time.
   * It also attaches keydown handlers to every <label>, and the custom swatch
   * is a label, so arrow keys inside it would jump focus to a variant option.
   *
   * So the component swallows its own change/input/keydown at its root. Our
   * own document listeners for those run in the CAPTURE phase, which happens
   * on the way down, before this ever fires.
   */
  var GUARDED = ['input', 'change', 'keydown'];

  function stopHere(e) { e.stopPropagation(); }

  function guardRoots() {
    var roots = document.querySelectorAll('[data-cc]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].hasAttribute('data-cc-guarded')) continue;
      roots[i].setAttribute('data-cc-guarded', '');
      for (var g = 0; g < GUARDED.length; g++) {
        roots[i].addEventListener(GUARDED[g], stopHere);
      }
    }
  }

  function syncUI() {
    guardRoots();

    var chosen = !!state.hex;
    var s = strings();

    // Belt and braces. This whole component lives inside the product form, and
    // a <button> with no type defaults to submit -- which here means Add To
    // Cart. The snippet sets type="button" on every one; this makes sure an
    // edit that forgets cannot turn a swatch into a checkout.
    var btns = document.querySelectorAll('[data-cc] button');
    for (var b = 0; b < btns.length; b++) {
      if (btns[b].type !== 'button') btns[b].type = 'button';
    }

    var inputs = document.querySelectorAll('[data-cc-input]');
    for (var i = 0; i < inputs.length; i++) {
      if (chosen && !isMyoc()) {
        inputs[i].setAttribute('name', 'properties[' + PROP + ']');
        inputs[i].value = casePropertyValue();
      } else {
        // An empty property would still reach the cart and show as a blank
        // line, so the name comes off entirely.
        inputs[i].removeAttribute('name');
        inputs[i].value = '';
      }
    }

    if (isMyoc()) syncMyocField(chosen);

    // The line above Add to Cart: "Your case will be printed in ● Alpine blue ✕".
    // Shown exactly when a Case Colour property is on the form, never else.
    var confirms = document.querySelectorAll('[data-cc-confirm]');
    for (var c = 0; c < confirms.length; c++) {
      var showConfirm = chosen && !isMyoc();
      confirms[c].hidden = !showConfirm;
      if (!showConfirm) continue;
      var cdot = confirms[c].querySelector('[data-cc-confirm-dot]');
      if (cdot) cdot.style.background = state.hex;
      var cname = confirms[c].querySelector('[data-cc-confirm-name]');
      if (cname) cname.textContent = state.name || state.hex;
    }

    syncAddButton(chosen);


    // Not [data-cc-name] -- every swatch button carries that as its own
    // metadata for the click handler, so this would write the current
    // selection's text into all twelve circles.
    var names = document.querySelectorAll('[data-cc-selected-name]');
    for (var n = 0; n < names.length; n++) {
      names[n].textContent = chosen
        ? (state.name ? state.name + ' \u00b7 ' + state.hex : state.hex)
        : (s.original || 'Original design');
    }

    // A dot for whatever is selected, named or not, so the chosen colour can
    // be SEEN next to its name -- the same thing the cart line shows.
    // state.hex has been through normalize(), which only returns a value
    // matching /^#[0-9a-f]{6}$/, so nothing else reaches this style property.
    var dots = document.querySelectorAll('[data-cc-dot]');
    for (var d = 0; d < dots.length; d++) {
      if (chosen) dots[d].style.background = state.hex;
      dots[d].hidden = !chosen;
    }

    var sws = document.querySelectorAll('[data-cc-hex], [data-cc-reset]');
    for (var k = 0; k < sws.length; k++) {
      var hex = sws[k].getAttribute('data-cc-hex');
      var on = chosen ? (normalize(hex) === state.hex) : sws[k].hasAttribute('data-cc-reset');
      sws[k].classList.toggle('is-on', on);
      sws[k].setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    var customs = document.querySelectorAll('[data-cc-custom]');
    for (var m = 0; m < customs.length; m++) {
      if (chosen) {
        try { customs[m].value = state.hex; } catch (e) { /* type=text fallback */ }
      }
      var wrap = customs[m].closest ? customs[m].closest('.cc__sw') : null;
      if (wrap) wrap.classList.toggle('is-on', chosen && !state.name);
    }

    syncHint();

    if (chosen && plates) schedule();
    else if (!chosen) hideOverlays();
  }

  // The MYOC "Custom Color" text field is the submitted property. A swatch
  // tap writes "Alpine blue (#1B4F9C)" into it; a silent choice leaves it
  // alone; a reset clears it only if the text there is still ours.
  function syncMyocField(chosen) {
    var f = document.querySelector('[data-myoc-colour-field]');
    if (!f) return;

    /*
     * THE FORM OWNER, OR THE COLOUR NEVER REACHES THE CART.
     *
     * product-cs-options.liquid renders form="product-form-<product>-<section>",
     * but the real form on the page is id="...---MainSection" -- the theme
     * appends that suffix. An input whose `form` attribute names an id that
     * does not exist belongs to NO form and is silently left out of the POST.
     *
     * The text fields get away with it because the snippet's own click handler
     * repoints every [js-product-cs-field] to the live form on any click,
     * which always happens before Add to cart. This input is hidden and no
     * longer carries that attribute (it has no text-field behaviour left), so
     * it does the same thing for itself, here -- on page load, on every colour
     * pick and on every remount.
     */
    // The input is rendered inside the product form now, so it needs nothing.
    // This is only for a page where it somehow sits outside one: binding by id
    // is the only thing that would make it submit there.
    if (!f.closest('form')) {
      var pf = document.querySelector('[js-product-form]');
      if (pf && pf.id && f.getAttribute('form') !== pf.id) f.setAttribute('form', pf.id);
    }
    if (chosen && userPicked) {
      var v = propertyValue();
      if (f.value !== v) {
        f.value = v;
        fieldWritten = v;
        f.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: 'cc' }));
      }
    } else if (!chosen && fieldWritten !== null && f.value === fieldWritten) {
      f.value = '';
      fieldWritten = null;
      f.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: 'cc' }));
    }
    var root = document.querySelector('[data-cc]');
    if (root) root.classList.add('cc--myoc');
  }

  function choose(hex, name, opts) {
    var value = normalize(hex);
    if (!value) return;
    var silent = !!(opts && opts.silent);
    // Re-applying the same colour silently must not strip the name the
    // customer's own pick gave it -- "Alpine blue (#1B4F9C)" would decay to
    // "#1B4F9C" in the cart.
    if (silent && !name && state.hex === value && state.name) name = state.name;
    state.hex = value;
    state.name = name || null;
    state.silent = silent;
    if (!silent) userPicked = true;
    syncUI();

    // Painting is off (an image would not decode). The CHOICE still stands:
    // syncUI above has already put it in the line-item property.
    if (failed) return;
    if (plates) { schedule(); return; }

    status(strings().loading || 'Building your preview...');
    // Six PNGs is several megabytes. On a shop where nearly all traffic is
    // mobile they are fetched on the first tap, never on page load.
    Promise.all([loadPlates(), loadArt(artFor(value).kind)])
      .then(function () { status(''); schedule(); })
      .catch(function (err) {
        /*
         * A PICTURE THAT WILL NOT LOAD MUST NOT UNPICK THE CUSTOMER'S COLOUR.
         *
         * This used to call reset(), which cleared state AND the line-item
         * property, so one 404 silently dropped the colour from the cart --
         * and `failed` then blocked every later pick, so it could not be put
         * back. Make Your Own Case hits it far more often than a premade
         * product does: its artwork comes from the preview service on Render,
         * whose cache is ephemeral, while a premade's plates are all on
         * Shopify's CDN.
         *
         * So: stop painting, say so, and leave the choice alone. The factory
         * works from the property, not from the canvas.
         */
        failed = true;
        hideOverlays();
        status(strings().failed || 'Preview unavailable.');
        if (window.console) console.warn('[colour-configurator]', err);
      });
  }

  function reset() {
    state.hex = null;
    state.name = null;
    state.silent = false;
    userPicked = false;
    var root = document.querySelector('[data-cc]');
    if (root) root.classList.remove('cc--picking');
    // On MYOC "no colour" is not a state the order can be in.
    if (isMyoc()) applyMyocDefault();
    syncUI();
  }

  /* ------------------------------------------------------------ runtime -- */

  // Public surface for myoc-preview.js. Everything else stays private; the
  // compositing maths in particular is not something to reach into.
  window.ccSetArt = function (urls) {
    runtimeArt = { light: (urls && urls.light) || null, dark: (urls && urls.dark) || null };
    art = {};
    artPromise = {};
    exported = null;
    failed = false;
    if (!state.hex) return;
    if (plates) schedule();
    else choose(state.hex, state.name, { silent: state.silent });
  };
  window.ccClearArt = function () {
    runtimeArt = null;
    art = {};
    artPromise = {};
    exported = null;
    hideOverlays();
  };
  window.ccHasArt = function () {
    var c = config();
    return !!(runtimeArt || (c && c.assets && (c.assets.light || c.assets.dark)));
  };
  window.ccChoose = function (hex, name, opts) { choose(hex, name, opts); };
  // After the theme has replaced the gallery (variant change) the painted
  // canvases are gone with it; this paints the kept state into the new
  // overlays. False when there is nothing to paint yet.
  window.ccRepaint = function () {
    if (!state.hex || !plates) return false;
    schedule();
    return true;
  };
  // The current render as a data URL, for the preview stage's zoom. `exported`
  // is the cache blit() clears on every repaint, so a colour change is never
  // shown stale.
  window.ccExport = function () {
    if (!off || !state.hex) return null;
    if (!exported) {
      try { exported = off.toDataURL('image/png'); } catch (e) { return null; }
    }
    return exported;
  };
  window.ccReset = function () { reset(); };
  window.ccState = function () {
    return { hex: state.hex, name: state.name, ready: !!plates,
             silent: !!state.silent, userPicked: userPicked };
  };

  /* ---------------------------------------------------------------- zoom -- */

  // The media id of the slide the gallery overlay lives in -- i.e. which
  // gallery image the recoloured canvas is standing in for.
  function overlayMediaId() {
    var list = document.querySelectorAll('[data-cc-overlay]');
    for (var i = 0; i < list.length; i++) {
      var slide = list[i].closest('[data-media-id]');
      if (slide) return slide.getAttribute('data-media-id');
    }
    return null;
  }

  // Swap the recoloured case into the viewer's media list. The lightbox calls
  // this once per open, so the canvas export happens on a zoom click rather
  // than on every repaint -- it costs tens of milliseconds and most visits
  // never zoom at all.
  // Chained, not assigned: myoc-ride.js installs its own override on Make
  // Your Own Case (narrowing the lightbox to the selected ride), and whichever
  // script loads second must not clobber the first. Ours runs first on the
  // full list, then hands on. If nothing else is installed, prev is undefined.
  var prevOverride = window.pzoomOverride;
  window.pzoomOverride = function (list) {
    var mine = colourOverride(list);
    if (typeof prevOverride === 'function') {
      try { return prevOverride(mine) || mine; } catch (e) { return mine; }
    }
    return mine;
  };
  function colourOverride(list) {
    if (!state.hex || !off) return list;
    var mid = overlayMediaId();
    if (mid === null) return list;
    if (!exported) {
      try { exported = off.toDataURL('image/png'); }
      catch (e) { return list; }
    }
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(mid)) {
        list[i] = {
          id: list[i].id,
          alt: list[i].alt,
          key: list[i].key,
          src: exported,
          full: exported,
          // Named at zoom size on every device: full screen there is no page
          // around the image to say what it is.
          caption: (strings().your_colour || 'Your colour') + ' · ' +
            (state.name ? state.name + ' (' + state.hex + ')' : state.hex)
        };
        break;
      }
    }
    return list;
  }

  /* -------------------------------------------------------------- events -- */

  // Tapping the mobile preview opens the same viewer as the gallery: small for
  // a glance, full screen when they want a proper look.
  // "Tap to see it closer" is worth saying once. After someone has opened the
  // preview they know, and a standing instruction is just clutter.
  var HINT_KEY = 'cc-preview-opened';

  function hintSeen() {
    try { return localStorage.getItem(HINT_KEY) === '1'; } catch (e) { return false; }
  }

  function syncHint() {
    var hints = document.querySelectorAll('[data-cc-hint]');
    var show = !!state.hex && !hintSeen();
    for (var i = 0; i < hints.length; i++) hints[i].hidden = !show;
  }

  function openPreview() {
    if (!state.hex) return;
    var mid = overlayMediaId();
    if (mid === null || !window.ProductZoom) return;
    try { localStorage.setItem(HINT_KEY, '1'); } catch (e) {}
    syncHint();
    window.ProductZoom.openByMediaId(mid);
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('.cc__preview')) openPreview();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (!e.target || !e.target.closest || !e.target.closest('.cc__preview')) return;
    e.preventDefault();
    openPreview();
  }, true);

  // The ✕ on the confirmation line: back to the original design, and the
  // property comes off the form with it (reset() -> syncUI()).
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (!e.target.closest('[data-cc-confirm-reset]')) return;
    e.preventDefault();
    reset();
  });

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var el = e.target.closest('[data-cc-hex], [data-cc-reset]');
    if (!el) return;
    e.preventDefault();
    if (el.hasAttribute('data-cc-reset')) reset();
    else choose(el.getAttribute('data-cc-hex'), el.getAttribute('data-cc-name'));
  });

  // A colour input only counts once the customer has actually reached for it.
  // Browsers fire `input` on their own when they restore a form value after a
  // reload or a back-navigation, and taking that at face value would paint a
  // case and attach a Case Colour property nobody asked for.
  function arm(e) {
    var el = e.target;
    if (el && el.hasAttribute && el.hasAttribute('data-cc-custom')) {
      el.setAttribute('data-cc-armed', '');
    }
  }
  document.addEventListener('pointerdown', arm, true);
  document.addEventListener('focus', arm, true);
  document.addEventListener('keydown', arm, true);

  /*
   * The OS colour sheet covers roughly the bottom two thirds of a phone
   * screen, and it cannot be moved or resized -- it is drawn by the system.
   * What can be moved is the page behind it, so the case goes to the top of
   * the viewport before the sheet appears. iOS fires `input` continuously
   * while the customer drags the spectrum, so they watch the case change live
   * in the strip above the sheet. The swatch row disappearing underneath is
   * the point: the sheet covers the controls, not the product.
   *
   * THIS MUST NEVER RUN ON pointerdown. A click lands on whatever is under the
   * pointer at pointerUP, so scrolling mid-gesture moves the page out from
   * under the finger and the tap lands somewhere else -- on mobile that was
   * sometimes Add To Cart, sitting just below the swatch grid. Scrolling only
   * after the gesture has finished is what keeps that impossible.
   */
  function stickyHeaderOffset() {
    var h = document.querySelector('[data-header-component], .header, header');
    if (!h) return 12;
    var pos = getComputedStyle(h).position;
    if (pos !== 'sticky' && pos !== 'fixed') return 12;
    return h.getBoundingClientRect().height + 12;
  }

  function revealForPicker() {
    if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) return;
    var pv = document.querySelector('.cc__preview');
    var root = document.querySelector('[data-cc]');
    if (isMyoc()) {
      // No picker preview on MYOC (swatches only); the case is in the gallery.
      var list = document.querySelectorAll('[data-cc-overlay]');
      pv = null;
      for (var g = 0; g < list.length; g++) {
        if (!list[g].closest('.cc__preview') && document.body.contains(list[g]) &&
            list[g].getBoundingClientRect().width > 0) { pv = list[g]; break; }
      }
    }
    if (!pv || !root) return;
    // The preview is hidden until a colour is chosen, so if this is the very
    // first pick there is nothing to scroll to yet. Show the empty frame now;
    // the first `input` fills it within a frame or two.
    root.classList.add('cc--picking');
    var top = pv.getBoundingClientRect().top + window.pageYOffset - stickyHeaderOffset();
    // Deliberately not smooth: the sheet opens within a couple of hundred ms
    // of this gesture and a smooth scroll loses that race, leaving the case
    // off screen underneath it.
    window.scrollTo(0, Math.max(0, top));
  }

  // One tap, one handler, deterministic order: stop the label's own activation,
  // move the page, then open the sheet ourselves. Staying inside this handler
  // keeps the user activation that showPicker() requires.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var lab = e.target.closest('.cc__sw--custom');
    if (!lab) return;
    var input = lab.querySelector('[data-cc-custom]');
    if (!input || e.target === input) return;

    var canShow = typeof input.showPicker === 'function';
    if (canShow) e.preventDefault();

    input.setAttribute('data-cc-armed', '');
    revealForPicker();

    if (canShow) {
      // Safari 16.4+ / Chrome 99+. Without it the label's native activation is
      // left alone and opens the sheet on this same tap.
      try { input.showPicker(); } catch (err) { input.click(); }
    }
  });

  // Sheet dismissed. If they backed out without picking anything, drop the
  // empty frame again rather than leaving a blank box on the page.
  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || !el.hasAttribute || !el.hasAttribute('data-cc-custom')) return;
    if (!state.hex) {
      var root = document.querySelector('[data-cc]');
      if (root) root.classList.remove('cc--picking');
    }
  }, true);

  document.addEventListener('input', function (e) {
    var el = e.target;
    if (!el || !el.hasAttribute || !el.hasAttribute('data-cc-custom')) return;
    if (!e.isTrusted || !el.hasAttribute('data-cc-armed')) return;
    choose(el.value, null);
  }, true);

  // The theme fires this after it has swapped the media container and every
  // product block, which is exactly when our hidden input and painted canvases
  // have just been thrown away.
  function boot() {
    if (isMyoc() && !state.hex) applyMyocDefault();
    syncUI();
  }

  document.addEventListener('tcustomizer-remount', boot);
  document.addEventListener('shopify:section:load', boot);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
